/**
 * Centralized Analytics — Frontend event tracking utility.
 * All components use this to fire events to /api/analytics.
 * Deduplication is handled server-side.
 * Auth is handled via httpOnly session cookie (credentials: 'include').
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let sessionId = null;

function getSessionId() {
  if (sessionId) return sessionId;
  sessionId = sessionStorage.getItem('lumera_session_id');
  if (!sessionId) {
    sessionId = crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem('lumera_session_id', sessionId);
  }
  return sessionId;
}

/**
 * Capture UTM parameters from URL on first load.
 * Stores in sessionStorage so they persist through the funnel.
 */
function captureUtmParams() {
  const stored = sessionStorage.getItem('lumera_utm');
  if (stored) return JSON.parse(stored);

  try {
    const params = new URLSearchParams(window.location.search);
    const utm = {};
    for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']) {
      const val = params.get(key);
      if (val) utm[key] = val;
    }
    // Also capture referrer on first visit
    if (document.referrer && !document.referrer.includes(window.location.hostname)) {
      utm.referrer = document.referrer;
    }
    if (Object.keys(utm).length > 0) {
      sessionStorage.setItem('lumera_utm', JSON.stringify(utm));
      return utm;
    }
  } catch { /* ignore */ }
  return {};
}

// Capture UTM immediately when module loads
const utmData = captureUtmParams();

/**
 * Get stored UTM data for this session.
 */
export function getUtmData() {
  try {
    const stored = sessionStorage.getItem('lumera_utm');
    return stored ? JSON.parse(stored) : {};
  } catch { return {}; }
}

/**
 * Track an analytics event (fire-and-forget).
 * UTM parameters are automatically attached from session.
 */
export async function trackEvent(eventType, data = {}) {
  const utm = getUtmData();
  const payload = {
    event_type: eventType,
    session_id: getSessionId(),
    target_slug: data.targetSlug || data.target_slug || null,
    target_type: data.targetType || data.target_type || null,
    price: data.price || null,
    currency: data.currency || null,
    payment_provider: data.paymentProvider || data.payment_provider || null,
    coupon_code: data.couponCode || data.coupon_code || null,
    source: data.source || utm.utm_source || null,
    referrer: data.referrer || utm.referrer || null,
    metadata: {
      ...(data.metadata || {}),
      ...(utm.utm_medium ? { utm_medium: utm.utm_medium } : {}),
      ...(utm.utm_campaign ? { utm_campaign: utm.utm_campaign } : {}),
      ...(utm.utm_content ? { utm_content: utm.utm_content } : {}),
      ...(utm.utm_term ? { utm_term: utm.utm_term } : {}),
    },
  };

  // Clean empty metadata
  if (payload.metadata && Object.keys(payload.metadata).length === 0) {
    payload.metadata = null;
  }

  try {
    const res = await fetch(`${API}/analytics/event/auth`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
      keepalive: true,
    });
    if (res.status === 401 || res.status === 403) {
      await fetch(`${API}/analytics/event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
        keepalive: true,
      });
    }
  } catch {
    // Fire-and-forget — never block UX
  }
}

// Convenience helpers
export const trackProductView = (slug) =>
  trackEvent('product_view', { targetSlug: slug, targetType: 'product' });

export const trackBundleView = (slug) =>
  trackEvent('bundle_view', { targetSlug: slug, targetType: 'bundle' });

export const trackPreviewStarted = (slug) =>
  trackEvent('preview_started', { targetSlug: slug, targetType: 'product' });

export const trackCheckoutStarted = (slug, type, price, currency, provider) =>
  trackEvent('checkout_started', { targetSlug: slug, targetType: type, price, currency, paymentProvider: provider });

export const trackCouponApplied = (slug, couponCode, discountAmount) =>
  trackEvent('coupon_applied', { targetSlug: slug, couponCode, metadata: { discount_amount: discountAmount } });

export const trackProUpgradeStarted = (interval, price, currency) =>
  trackEvent('pro_upgrade_started', { targetSlug: interval === 'yearly' ? 'pro_yearly' : 'pro', targetType: 'subscription', price, currency });

export const trackCrossSellViewed = (slug) =>
  trackEvent('cross_sell_viewed', { targetSlug: slug, targetType: 'product' });

export const trackCrossSellPurchased = (slug) =>
  trackEvent('cross_sell_purchased', { targetSlug: slug, targetType: 'product' });

// Smart Recommendation tracking
export const trackRecImpression = (slug, type, placement, reason) =>
  trackEvent('rec_impression', { targetSlug: slug, targetType: type, metadata: { placement, reason } });

export const trackRecClick = (slug, type, placement, reason) =>
  trackEvent('rec_click', { targetSlug: slug, targetType: type, metadata: { placement, reason } });

export const trackRecCheckoutStarted = (slug, type, placement, price, currency) =>
  trackEvent('rec_checkout_started', { targetSlug: slug, targetType: type, price, currency, metadata: { placement } });

export const trackRecPurchaseCompleted = (slug, type, placement, price, currency) =>
  trackEvent('rec_purchase_completed', { targetSlug: slug, targetType: type, price, currency, metadata: { placement } });
